import { useCallback, useEffect, useRef, useState } from 'react';
import type { UserSystemVersionCountDTO } from '../types/users.types';
import { useSearchParams } from 'react-router-dom';
import { isApiError } from '@aiptyg/types';
import * as usersService from '../services/users.service';
import type { AdminUserListItemDTO, AppleVerificationStatus, BooleanFilterValue } from '../types/users.types';

const ERROR_MAP: Record<string, string> = {
  admin_role_required: '无管理员权限',
  'invalid user role': '角色筛选不合法',
  'invalid user status': '状态筛选不合法',
  'invalid apple verification status filter': 'Apple 验证状态筛选不合法',
  network_error: '网络连接失败，请检查网络',
};

function resolveError(error: unknown): string {
  if (isApiError(error)) {
    return ERROR_MAP[error.message] ?? `请求失败 (${error.message})`;
  }
  return '未知错误';
}

export function useAdminUserList() {
  const [searchParams, setSearchParams] = useSearchParams();

  const marketId = searchParams.get('marketId') || '';
  const keyword = searchParams.get('keyword') || '';
  const role = searchParams.get('role') || '';
  const status = searchParams.get('status') || '';
  const createdFrom = searchParams.get('createdFrom') || '';
  const createdTo = searchParams.get('createdTo') || '';
  const hasInviter = (searchParams.get('hasInviter') || '') as BooleanFilterValue;
  const appleVerificationStatus = (searchParams.get('appleVerificationStatus') || '') as AppleVerificationStatus;
  const riskTagged = (searchParams.get('riskTagged') || '') as BooleanFilterValue;
  const page = parseInt(searchParams.get('page') || '1', 10) || 1;
  const rawPageSize = parseInt(searchParams.get('pageSize') || '20', 10) || 20;
  const pageSize = [20, 50, 100, 1000].includes(rawPageSize) ? rawPageSize : 20;

  const requestSequence = useRef(0);
  const [systemVersionCounts, setSystemVersionCounts] = useState<UserSystemVersionCountDTO[]>([]);
  const [items, setItems] = useState<AdminUserListItemDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value !== null && value !== '') {
          next.set(key, value);
        } else {
          next.delete(key);
        }
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(async (
    targetPage: number,
    currentKeyword: string,
    currentRole: string,
    currentStatus: string,
    currentCreatedFrom: string,
    currentCreatedTo: string,
    currentHasInviter: BooleanFilterValue,
    currentAppleVerificationStatus: AppleVerificationStatus,
    currentRiskTagged: BooleanFilterValue,
  ) => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError(null);
    try {
      const result = await usersService.listAdminUsers({
        marketId,
        page: targetPage,
        pageSize,
        keyword: currentKeyword,
        role: currentRole,
        status: currentStatus,
        createdFrom: currentCreatedFrom,
        createdTo: currentCreatedTo,
        hasInviter: currentHasInviter,
        appleVerificationStatus: currentAppleVerificationStatus,
        riskTagged: currentRiskTagged,
      });
      if (sequence !== requestSequence.current) return;
      setSystemVersionCounts(result.system_version_counts ?? []);
      setItems(result.items ?? []);
      setTotal(result.total);
    } catch (e) {
      if (sequence !== requestSequence.current) return;
      setSystemVersionCounts([]);
      setError(resolveError(e));
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [pageSize, marketId]);

  useEffect(() => {
    void load(
      page,
      keyword,
      role,
      status,
      createdFrom,
      createdTo,
      hasInviter,
      appleVerificationStatus,
      riskTagged,
    );
  }, [load, page, keyword, role, status, createdFrom, createdTo, hasInviter, appleVerificationStatus, riskTagged]);

  const setKeyword = useCallback((value: string) => {
    updateParams({ keyword: value || null, page: null });
  }, [updateParams]);

  const setMarketId = useCallback((value: string) => {
    updateParams({ marketId: value || null, page: null });
  }, [updateParams]);

  const setRole = useCallback((value: string) => {
    updateParams({ role: value || null, page: null });
  }, [updateParams]);

  const setStatus = useCallback((value: string) => {
    updateParams({ status: value || null, page: null });
  }, [updateParams]);

  const setCreatedFrom = useCallback((value: string) => {
    updateParams({ createdFrom: value || null, page: null });
  }, [updateParams]);

  const setCreatedTo = useCallback((value: string) => {
    updateParams({ createdTo: value || null, page: null });
  }, [updateParams]);

  const setHasInviter = useCallback((value: BooleanFilterValue) => {
    updateParams({ hasInviter: value || null, page: null });
  }, [updateParams]);

  const setAppleVerificationStatus = useCallback((value: AppleVerificationStatus) => {
    updateParams({ appleVerificationStatus: value || null, page: null });
  }, [updateParams]);

  const setRiskTagged = useCallback((value: BooleanFilterValue) => {
    updateParams({ riskTagged: value || null, page: null });
  }, [updateParams]);

  const setPage = useCallback((value: number) => {
    updateParams({ page: value > 1 ? String(value) : null });
  }, [updateParams]);

  const setPageSize = useCallback((value: number) => {
    updateParams({ pageSize: value === 20 ? null : String(value), page: null });
  }, [updateParams]);

  const resetFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const reload = useCallback(() => {
    void load(
      page,
      keyword,
      role,
      status,
      createdFrom,
      createdTo,
      hasInviter,
      appleVerificationStatus,
      riskTagged,
    );
  }, [load, page, keyword, role, status, createdFrom, createdTo, hasInviter, appleVerificationStatus, riskTagged]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return {
    systemVersionCounts,
    marketId,
    setMarketId,
    items,
    total,
    page,
    pageSize,
    totalPages,
    keyword,
    role,
    status,
    createdFrom,
    createdTo,
    hasInviter,
    appleVerificationStatus,
    riskTagged,
    loading,
    error,
    setKeyword,
    setRole,
    setStatus,
    setCreatedFrom,
    setCreatedTo,
    setHasInviter,
    setAppleVerificationStatus,
    setRiskTagged,
    setPage,
    setPageSize,
    resetFilters,
    reload,
  };
}
